"use client";

import { UserGroupIcon, CpuChipIcon, CircleStackIcon, ChartBarIcon } from '@heroicons/react/24/outline';

export default function TeamExpertiseSection() {
  const teamCategories = [
    {
      icon: CpuChipIcon,
      title: 'DATA ARCHITECTS',
      description: 'Design the overall data solution, choose technologies, create data models, and plan performance optimization strategies.'
    },
    {
      icon: CircleStackIcon,
      title: 'SENIOR DATA ENGINEERS',
      description: 'Build data pipelines, integrate systems, manage data transformations, and maintain production systems for reliability.'
    },
    {
      icon: UserGroupIcon,
      title: 'ETL SPECIALISTS',
      description: 'Connect ERP and legacy systems, clean and validate data, manage data migration, and ensure governance compliance.'
    },
    {
      icon: ChartBarIcon,
      title: 'BI DEVELOPERS',
      description: 'Build dashboards and reports, help teams use analytics, automate reporting workflows, and support business users.'
    }
  ];

  const certifications = [
    'AWS Data Analytics',
    'Azure Data Engineer',
    'Snowflake',
    'Google Cloud Data Engineer',
    'Tableau',
    'dbt'
  ];

  return (
    <section className="bg-white py-12 sm:py-16 md:py-24 px-4 sm:px-6 min-h-0">
      <div className="max-w-7xl mx-auto w-full">
        {/* Header */}
        <div className="text-left md:text-center mb-10 sm:mb-16">
          <p className="text-gold text-xs sm:text-sm font-semibold uppercase tracking-[1.5px] mb-3 sm:mb-4">
            OUR TEAM
          </p>
          <h2 className="font-serif text-navy text-[26px] sm:text-[36px] md:text-[48px] leading-[1.2] font-bold mb-4 sm:mb-6">
            Team and Expertise
          </h2>
          <p className="text-charcoal text-base sm:text-lg md:text-xl leading-[1.7] max-w-3xl md:mx-auto">
            Our team includes experts across architecture, engineering, ETL, and analytics who bring deep technical knowledge and real-world experience to every project.
          </p>
        </div>

        {/* Team Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8 mb-12 sm:mb-20">
          {teamCategories?.map((category, index) => {
            const IconComponent = category?.icon;
            return (
              <div
                key={index}
                className="bg-cream rounded-xl p-5 sm:p-6 md:p-8 border border-gray-200 hover:border-gold transition-all duration-300"
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gold/20 flex items-center justify-center">
                    <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-gold" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-navy text-xs sm:text-sm font-bold uppercase tracking-[1.5px] mb-2 sm:mb-3">
                      {category?.title}
                    </h3>
                    <p className="text-charcoal text-[14px] sm:text-base leading-[1.7]">
                      {category?.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Certifications */}
        <div className="bg-navy rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 text-left md:text-center">
          <h3 className="font-serif text-white text-xl sm:text-2xl font-bold mb-5 sm:mb-8">
            Team Certifications
          </h3>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6">
            {certifications?.map((cert, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 sm:px-6 py-2.5 sm:py-3 text-white text-sm sm:text-base font-medium hover:bg-white/20 transition-all duration-300"
              >
                {cert}
              </div>
            ))}
          </div>
          <p className="text-white/70 text-xs sm:text-sm mt-6 sm:mt-8 max-w-2xl md:mx-auto">
            Our team maintains current certifications across all major data platforms and continuously updates skills to stay ahead of industry trends.
          </p>
        </div>
      </div>
    </section>
  );
}